import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneIcon, XIcon, SendIcon, MessageCircleIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getUTMDataForSubmission, appendUTMToUrl } from '../utils/utm';
const WORKER_URL = 'https://white-bonus-e94a.maxkhmlyt.workers.dev';
const PHONE_NUMBER = '380508399309';
const TELEGRAM_LINK = `https://t.me/+${PHONE_NUMBER}`;
const WHATSAPP_LINK = `https://wa.me/${PHONE_NUMBER}`;
const COUNTRY_CODES = [{
  code: '+380',
  flag: '🇺🇦',
  country: 'Ukraine'
}, {
  code: '+375',
  flag: '🇧🇾',
  country: 'Belarus'
}, {
  code: '+372',
  flag: '🇪🇪',
  country: 'Estonia'
}, {
  code: '+371',
  flag: '🇱🇻',
  country: 'Latvia'
}, {
  code: '+370',
  flag: '🇱🇹',
  country: 'Lithuania'
}, {
  code: '+48',
  flag: '🇵🇱',
  country: 'Poland'
}, {
  code: '+49',
  flag: '🇩🇪',
  country: 'Germany'
}, {
  code: '+44',
  flag: '🇬🇧',
  country: 'UK'
}, {
  code: '+43',
  flag: '🇦🇹',
  country: 'Austria'
}, {
  code: '+41',
  flag: '🇨🇭',
  country: 'Switzerland'
}, {
  code: '+39',
  flag: '🇮🇹',
  country: 'Italy'
}, {
  code: '+34',
  flag: '🇪🇸',
  country: 'Spain'
}, {
  code: '+33',
  flag: '🇫🇷',
  country: 'France'
}, {
  code: '+1',
  flag: '🇺🇸',
  country: 'USA/Canada'
}, {
  code: '+7',
  flag: '🇷🇺',
  country: 'Russia/Kazakhstan'
}, {
  code: '+972',
  flag: '🇮🇱',
  country: 'Israel'
}, {
  code: '+971',
  flag: '🇦🇪',
  country: 'UAE'
}, {
  code: '+995',
  flag: '🇬🇪',
  country: 'Georgia'
}, {
  code: '+90',
  flag: '🇹🇷',
  country: 'Turkey'
}].sort((a, b) => b.code.length - a.code.length);
export function CallbackWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const {
    t,
    language,
    getPath
  } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const widgetTitle: Record<string, string> = {
    en: 'Free Consultation',
    ru: 'Бесплатная консультация',
    ua: 'Безкоштовна консультація',
    cz: 'Bezplatná konzultace'
  };
  const consentText: Record<string, string> = {
    en: 'I agree to the processing of my personal data',
    ru: 'Я согласен на обработку моих персональных данных',
    ua: 'Я погоджуюсь на обробку моїх персональних даних',
    cz: 'Souhlasím se zpracováním osobních údajů'
  };
  const consentErrorText: Record<string, string> = {
    en: 'Please agree to continue',
    ru: 'Пожалуйста, дайте согласие для продолжения',
    ua: 'Будь ласка, дайте згоду для продовження',
    cz: 'Pro pokračování je nutný souhlas'
  };
  const languageNames: Record<string, string> = {
    en: 'English',
    ru: 'Русский',
    ua: 'Українська',
    cz: 'Čeština'
  };
  const detectedCountry = useMemo(() => {
    const phone = formData.phone.replace(/\s/g, '');
    if (!phone.startsWith('+')) return null;
    for (const country of COUNTRY_CODES) {
      if (phone.startsWith(country.code)) return country;
    }
    return null;
  }, [formData.phone]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleConsentChange = () => {
    setConsent(!consent);
    if (!consent) setConsentError(false);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consent) {
      setConsentError(true);
      return;
    }
    setIsSubmitting(true);
    const utmData = getUTMDataForSubmission();
    try {
      await fetch(WORKER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message || 'Не указано',
          form_type: 'Виджет обратной связи',
          page_url: window.location.href,
          language: languageNames[language] || language,
          ...utmData
        })
      });
    } catch {
      // Ignore
    }
    const thankYouPath = getPath('thank-you');
    window.location.href = appendUTMToUrl(thankYouPath);
  };
  return <>
      {/* Floating phone button */}
      <motion.button onClick={() => setIsOpen(true)} className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full flex items-center justify-center shadow-lg" style={{
      backgroundColor: 'var(--accent)'
    }} whileHover={{
      scale: 1.1
    }} whileTap={{
      scale: 0.95
    }} animate={!isOpen ? {
      scale: [1, 1.05, 1]
    } : {}} transition={!isOpen ? {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut'
    } : {}} aria-label="Open callback widget" data-id="element-31">
        <PhoneIcon className="w-6 h-6 text-white" data-id="element-32" />
      </motion.button>

      {/* Widget panel */}
      <AnimatePresence data-id="element-33">
        {isOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm md:bg-transparent md:backdrop-blur-none" onClick={() => setIsOpen(false)} data-id="element-34" />

            {/* Panel */}
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.95
        }} transition={{
          duration: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} className="fixed z-50 bottom-0 right-0 w-full sm:bottom-24 sm:right-6 sm:w-[400px] sm:rounded-2xl rounded-t-2xl border shadow-2xl overflow-hidden max-h-[85vh] flex flex-col" style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--border)'
        }} data-id="element-35">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0" style={{
            borderColor: 'var(--border)'
          }} data-id="element-36">
                <div className="flex items-center gap-3" data-id="element-37">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{
                backgroundColor: 'rgba(59, 130, 246, 0.15)'
              }} data-id="element-38">
                    <PhoneIcon className="w-4 h-4" style={{
                  color: 'var(--accent)'
                }} data-id="element-39" />
                  </div>
                  <h3 className="text-sm font-medium" style={{
                color: 'var(--text-primary)'
              }} data-id="element-40">
                    {widgetTitle[language] || widgetTitle.en}
                  </h3>
                </div>
                <button onClick={() => setIsOpen(false)} className="p-1.5 rounded-lg transition-colors hover:bg-black/5" aria-label="Close" data-id="element-41">
                  <XIcon className="w-5 h-5" style={{
                color: 'var(--text-muted)'
              }} data-id="element-42" />
                </button>
              </div>

              {/* Scrollable content */}
              <div className="overflow-y-auto flex-1 px-5 py-5" data-id="element-43">
                {/* Messenger buttons */}
                <div className="flex gap-3 mb-5" data-id="element-44">
                  <a href={TELEGRAM_LINK} target="_blank" rel="noopener noreferrer" className="flex flex-1 items-center justify-center gap-2 rounded-xl py-3 font-medium border text-sm transition-all hover:scale-[1.02]" style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)'
              }} data-id="element-45">
                    <SendIcon className="h-4 w-4" style={{
                  color: 'var(--accent)'
                }} data-id="element-46" />
                    Telegram
                  </a>
                  <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="flex flex-1 items-center justify-center gap-2 rounded-xl py-3 font-medium border text-sm transition-all hover:scale-[1.02]" style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)'
              }} data-id="element-47">
                    <MessageCircleIcon className="h-4 w-4" style={{
                  color: 'var(--accent)'
                }} data-id="element-48" />
                    WhatsApp
                  </a>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3 mb-5" data-id="element-49">
                  <div className="h-px flex-1" style={{
                backgroundColor: 'var(--border)'
              }} data-id="element-50" />
                  <span className="text-xs" style={{
                color: 'var(--text-muted)'
              }} data-id="element-51">
                    {t('consultation.or')}
                  </span>
                  <div className="h-px flex-1" style={{
                backgroundColor: 'var(--border)'
              }} data-id="element-52" />
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4" data-id="element-53">
                  <div data-id="element-54">
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" style={{
                  borderColor: 'var(--border)',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)'
                }} placeholder={t('consultation.namePlaceholder')} data-id="element-55" />
                  </div>

                  <div data-id="element-56">
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" style={{
                  borderColor: 'var(--border)',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)'
                }} placeholder={t('consultation.emailPlaceholder')} data-id="element-57" />
                  </div>

                  <div className="relative" data-id="element-58">
                    {detectedCountry && <div className="absolute left-3 top-1/2 -translate-y-1/2 text-lg pointer-events-none" title={detectedCountry.country} data-id="element-59">
                        {detectedCountry.flag}
                      </div>}
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" style={{
                  borderColor: 'var(--border)',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  paddingLeft: detectedCountry ? '2.5rem' : '1rem'
                }} placeholder="+1 234 567 8900" data-id="element-60" />
                  </div>

                  <div data-id="element-61">
                    <textarea name="message" value={formData.message} onChange={handleChange} rows={3} className="w-full resize-none rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" style={{
                  borderColor: 'var(--border)',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)'
                }} placeholder={t('consultation.descPlaceholder')} data-id="element-62" />
                  </div>

                  {/* Consent */}
                  <div className={`flex items-start gap-2.5 p-2.5 rounded-xl ${consentError ? 'bg-red-500/10' : ''}`} data-id="element-63">
                    <button type="button" onClick={handleConsentChange} className="flex-shrink-0 w-4.5 h-4.5 mt-0.5 rounded border-2 flex items-center justify-center" style={{
                  width: '18px',
                  height: '18px',
                  borderColor: consentError ? '#ef4444' : consent ? 'var(--accent)' : 'var(--border)',
                  backgroundColor: consent ? 'var(--accent)' : 'transparent'
                }} data-id="element-64">
                      {consent && <CheckIcon className="w-3 h-3 text-white" data-id="element-65" />}
                    </button>
                    <div className="flex-1" data-id="element-66">
                      <label className="text-xs cursor-pointer leading-tight" style={{
                    color: consentError ? '#ef4444' : 'var(--text-secondary)'
                  }} onClick={handleConsentChange} data-id="element-67">
                        {consentText[language] || consentText.en}
                      </label>
                      {consentError && <p className="text-xs mt-0.5 font-medium" style={{
                    color: '#ef4444'
                  }} data-id="element-68">
                          {consentErrorText[language] || consentErrorText.en}
                        </p>}
                    </div>
                  </div>

                  <button type="submit" disabled={isSubmitting} className="w-full rounded-xl py-3.5 font-medium text-sm transition-all hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed" style={{
                backgroundColor: 'var(--accent)',
                color: '#FFFFFF'
              }} data-id="element-69">
                    <span className="flex items-center justify-center gap-2" data-id="element-70">
                      {isSubmitting ? t('consultation.sending') : <>
                          {t('consultation.cta')}
                          <ArrowRightIcon className="h-4 w-4" data-id="element-71" />
                        </>}
                    </span>
                  </button>
                </form>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}